/**
 * GlobalLoading component
 * Displays a full-screen loading overlay with shimmer effect
 * Connected to Redux state for centralized loading state management
 */
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { connect } from 'react-redux';
import { RootState } from '../../redux/store';
import { LoadingState } from '../../interfaces/connected-components/loading';
import { tr } from '../../translation';

/**
 * Fade-in animation for the loading overlay
 */
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

/**
 * Full-screen overlay container for the loading indicator
 * Controls visibility with smooth transitions
 */
const Container = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999999;
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  background-color: ${({ theme }) => theme.overlay};
  backdrop-filter: blur(5px);
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  animation: ${fadeIn} 0.3s ease-in-out;
`;

/**
 * Shimmer animation for the loading text
 */
const shimmer = keyframes`
    0% {
        background-position: 200% center;
    }
    100% {
        background-position: -200% center;
    }
`;

/**
 * Loading text with a shimmer effect
 * Uses a gradient background with animation to create a motion effect
 */
const LoadingText = styled.div`
  font-size: ${({ theme }) => theme.text.s7};
  font-weight: bold;
  font-family: Arial;
  background: linear-gradient(-90deg, #7a7a7a 0%, ${({ theme }) => theme.text.shine} 50%, #7a7a7a 100%);
  background-size: 200% auto;
  animation: ${shimmer} 2s linear infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/**
 * GlobalLoading component that shows a fullscreen loading overlay
 * Controlled by Redux state, with smooth enter/exit animations
 *
 * @param loading - The loading state from Redux
 * @returns Loading overlay component that displays when loading state is active
 */
const GlobalLoading = ({ loading }: { loading: LoadingState }) => {
  const { isVisible } = loading || {};
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Handle mounting/unmounting with animation delay
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isVisible) {
      setIsModalVisible(true);
    } else {
      timeout = setTimeout(() => setIsModalVisible(false), 300);
    }
    document.body.style.overflow = isVisible ? 'hidden' : '';
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isVisible]);

  if (!isModalVisible) return null;

  return (
    <Container isVisible={isVisible}>
      <LoadingText>{tr('app.loading')}</LoadingText>
    </Container>
  );
};

/**
 * Maps the Redux state to component props
 *
 * @param state - The Redux store state
 * @returns Object with the loading state
 */
const mapStateToProps = (state: RootState) => ({
  loading: state?.loading,
});

export default connect(mapStateToProps)(GlobalLoading);
